import { type BoundingBoxContentType } from '../../../../../shared/bounding-box-content.js';
import type { SolutionSource } from '../../../../../shared/solution-source.js';
import type { SaveTarget } from '../grading/utils.js';

// --- Interfaces ---
export interface Task {
  id?: string;
  name: string;
  taskNumber?: string;
  taskType?: string;
  extractedText: string;
  imageIds?: string[];
  /** JSON-serialized array of {pageNumber, x, y, width, height} — see MasterSolutionDTO.descriptionBoxesJson.
   * Drives the full-page + overlay task excerpt view in wide canvas mode. */
  descriptionBoxesJson?: string | null;
  masterSolutionImageIds?: string[];
  gradingSchemeImageIds?: string[];
  masterSolutionConfidence?: number;
  gradingSchemeConfidence?: number;
  masterSolutionIsApproved?: boolean;
  gradingSchemeIsApproved?: boolean;
  /** Per-task provenance — 'upload' | 'generated'. Undefined when the task has no content for that section. */
  masterSolutionSource?: SolutionSource;
  gradingSchemeSource?: SolutionSource;
  masterSolution: string;
  gradingCriteria?: string;
  gradingMaxPoints?: number;
  descriptionContentType?: BoundingBoxContentType;
  masterSolutionContentType?: BoundingBoxContentType;
  gradingSchemeContentType?: BoundingBoxContentType;
  needsAnswer?: boolean;
  /** JSON-serialized array of {_id, criteria, maxPoints, confidence, isApproved, dependsOn} — see
   * MasterSolutionDTO. Every language-use rule is a LanguageUseScheme now (solo 1-member or
   * multi-member); a task can belong to more than one. */
  languageUseGroupsJson?: string | null;
}

/**
 * True when a task's grading-scheme CONTENT already exists (criteria text or an image box) but its own
 * points are <= 0 — mirrors the backend's zeroPointsTaskPaths (evaluateGradingSchemeComplete /
 * ownMaxPoints), which excludes tasks with no grading-scheme section at all. Those aren't a problem yet,
 * just not generated/extracted — flagging them would count every not-yet-processed task as "uncertain".
 * Checked ahead of `gradingSchemeIsApproved` everywhere it's used: approving doesn't fix a 0-point task's
 * inability to be graded (the backend completeness gate ignores isApproved too), so approval must not be
 * able to silence this signal.
 */
export function hasZeroContentPoints(task: Task): boolean {
  if (task.needsAnswer === false) return false;
  const hasContent =
    (typeof task.gradingCriteria === 'string' && task.gradingCriteria.trim().length > 0) ||
    (Array.isArray(task.gradingSchemeImageIds) && task.gradingSchemeImageIds.length > 0);
  return hasContent && (task.gradingMaxPoints ?? 0) <= 0;
}

/** A description bounding box in page-percent coordinates — see MasterSolutionDTO.descriptionBoxesJson. */
export interface TaskExcerptBox {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export function parseDescriptionBoxesJson(raw: string | null | undefined): TaskExcerptBox[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (box): box is TaskExcerptBox =>
        !!box &&
        typeof box.pageNumber === 'number' &&
        typeof box.x === 'number' &&
        typeof box.y === 'number' &&
        typeof box.width === 'number' &&
        typeof box.height === 'number',
    );
  } catch {
    return [];
  }
}

export interface SolutionGradingCheckProps {
  examId: string;
  tasks: Task[];
  /** Full original exam page images (blank exam), in page order — same source as the structure editor's
   * `pages` prop. Used by the task excerpt panel to render all pages with the current task's description
   * box(es) overlaid, in wide canvas mode. Empty/omitted falls back to the cropped-image view. */
  pages?: string[];
  currentTaskIndex: number;
  onTaskChange: (index: number) => void;
  onUpdateMasterSolution: (index: number, newSolution: string) => void;
  onUpdateGradingScheme: (index: number, grading: { criteria: string; maxPoints: number }, target?: SaveTarget) => void;
  onApproveMasterSolution?: (index: number) => void;
  onApproveGradingScheme?: (index: number, target?: SaveTarget) => void;
  onDeleteSolutionImage?: (index: number, imageId: string, type: 'masterSolution' | 'gradingScheme') => void;
  onAddSolutionImage?: (index: number, imageId: string, type: 'masterSolution' | 'gradingScheme') => void;
  /** Commits a LanguageUseScheme group's full next membership (task ids). Resolves on success, rejects
   * on failure so the membership-picker popover can show an error and keep the draft open. */
  onUpdateLanguageUseGroupMembership?: (languageUseSchemeId: string, nextTaskIds: string[]) => Promise<void>;
  onDeleteLanguageUseGroup?: (languageUseSchemeId: string) => Promise<void>;
  /** True while a grading run is in progress for this exam — deleting a LanguageUseScheme mid-run can
   * race the run's in-memory rule snapshot, so the delete action is disabled while this is true. */
  isGradingInProgress?: boolean;
  /** Task ids currently being (re)generated by a live msExtraction/msGeneration/msRefine job — drives the
   * MS box spinner. Sourced from streamLiveSolutionJobsByExamIdSubscription (see get-live-solution-jobs.ts):
   * every task id while an msExtraction job is live (one row per exam, so the whole artifact spins
   * together), the exact target node ids from a live msGeneration row's payload, or the requested tasks
   * (resolved to ids) from a live msRefine row. Keyed by each gradable node's OWN id (matching backend
   * getTaskKey), not its root's — a call covers a whole main-task subtree, but rarely targets every node. */
  refiningMasterSolutionTaskIds?: Set<string>;
  /** Same sourcing as refiningMasterSolutionTaskIds, for gsExtraction/gsGeneration/gsRefine(target:
   * 'content'|absent) — drives the GS box spinner in content mode. */
  refiningGradingSchemeTaskIds?: Set<string>;
  /** Same sourcing again, but for gsRefine(target: 'languageUse') specifically (plus gsExtraction/
   * gsGeneration, which legitimately touch both dimensions at once) — drives the GS box spinner in
   * languageUse mode. Kept as its own set (not folded into refiningGradingSchemeTaskIds) so a content
   * refine can never light up the languageUse box and vice versa — the one thing SolutionProgress
   * couldn't express, since it has no `target` field. */
  refiningGradingSchemeLanguageUseTaskIds?: Set<string>;
}

export interface TaskGroup {
  mainNum: string;
  mainTaskIndex: number | null;
  mainNeedsAnswer: boolean;
  subTasks: { flatIndex: number; subNum: string; needsAnswer: boolean }[];
}

// --- Regex & Parsing ---

// Synonyms for "points" used in German grading rubrics. Single source of truth —
// add new variants here. Order matters: longer alternatives must come first so
// the regex doesn't stop at a shorter prefix (e.g. "Pkt" before "P").
// Strings are regex-source fragments, so `\\.?` denotes an optional literal dot.
export const POINT_SYNONYM_PATTERNS = ['Bewertungseinheiten?', 'Punkte?', 'Pkt\\.?', 'BE', 'P'] as const;

const POINT_SYNONYMS_GROUP = POINT_SYNONYM_PATTERNS.join('|');

// Match "<number> <synonym>", e.g. "2,5 Punkte", "3 P", "1 BE", "0,5 Bewertungseinheiten".
// The negative lookahead prevents partial matches like "P" inside "Personen" or "BE" in "betrifft".
const POINTS_REGEX = new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*(${POINT_SYNONYMS_GROUP})(?![A-Za-zÄÖÜäöüß])`, 'gi');

interface PointsMatch {
  start: number;
  end: number;
  value: number;
  unit: string;
}

export function extractPointsMatches(criteria: string): PointsMatch[] {
  const matches: PointsMatch[] = [];
  const regex = new RegExp(POINTS_REGEX.source, 'gi');
  let match: RegExpExecArray | null = regex.exec(criteria);

  while (match) {
    const numberRaw = match[1]?.replace(',', '.') || '0';
    const parsed = Number(numberRaw);

    if (Number.isFinite(parsed) && parsed >= 0 && typeof match.index === 'number') {
      const start = match.index;
      const end = start + match[0].length;
      matches.push({ start, end, value: parsed, unit: match[2] || 'Punkte' });
    }
    match = regex.exec(criteria);
  }
  return matches;
}

export function formatScaledPoints(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/\.00$/, '').replace(/0$/, '').replace('.', ',');
}

export function snapToHalfStep(value: number): number {
  return Math.round(value * 2) / 2;
}

// enforceTotal: when true (default) the balancing loop forces the scaled values to sum
// exactly to targetMaxPoints. Pass false when the criteria contain natural-language points
// that the regex misses — balancing would over-inflate the captured values in that case.
export function scaleCriteriaPoints(
  criteria: string,
  factor: number,
  targetMaxPoints: number,
  enforceTotal = true,
): string {
  const matches = extractPointsMatches(criteria);
  if (!matches.length) return criteria;
  if (!Number.isFinite(factor) || factor < 0) return criteria;

  const targetHalfPoints = Math.max(0, Math.round(targetMaxPoints * 2));
  const rawHalfPoints = matches.map(item => item.value * factor * 2);
  const roundedHalfPoints = rawHalfPoints.map(value => Math.max(0, Math.round(value)));

  const sumRounded = roundedHalfPoints.reduce((sum, value) => sum + value, 0);
  let diff = targetHalfPoints - sumRounded;

  if (enforceTotal && diff !== 0) {
    const ranking = rawHalfPoints
      .map((rawValue, index) => ({ index, delta: rawValue - (roundedHalfPoints[index] ?? 0) }))
      .sort((a, b) => (diff > 0 ? b.delta - a.delta : a.delta - b.delta));

    let guard = 0;
    while (diff !== 0 && guard < 10000) {
      let changedInCycle = false;
      for (const ranked of ranking) {
        if (diff === 0) break;
        const current = roundedHalfPoints[ranked.index] ?? 0;
        if (diff > 0) {
          roundedHalfPoints[ranked.index] = current + 1;
          diff -= 1;
          changedInCycle = true;
        } else if (current > 0) {
          roundedHalfPoints[ranked.index] = current - 1;
          diff += 1;
          changedInCycle = true;
        }
      }
      if (!changedInCycle) break;
      guard += 1;
    }
  }

  let rebuilt = '';
  let cursor = 0;
  for (let index = 0; index < matches.length; index += 1) {
    const current = matches[index];
    if (!current) continue;
    const halfPoints = roundedHalfPoints[index] ?? 0;
    const scaledValue = halfPoints / 2;
    rebuilt += criteria.slice(cursor, current.start);
    rebuilt += `${formatScaledPoints(scaledValue)} ${current.unit}`;
    cursor = current.end;
  }

  rebuilt += criteria.slice(cursor);
  return rebuilt;
}

// --- Task Grouping Logic ---
export function buildTaskGroups(tasks: Task[]) {
  const taskGroups: TaskGroup[] = [];
  const groupByMain: Record<string, TaskGroup> = {};

  tasks.forEach((task, index) => {
    const num = task.taskNumber ?? task.name.replace(/^Aufgabe\s+/i, '').trim();
    const dotIdx = num.indexOf('.');
    const mainNum = dotIdx === -1 ? num : num.slice(0, dotIdx);
    const subNum = dotIdx === -1 ? '' : num.slice(dotIdx + 1);

    if (!groupByMain[mainNum]) {
      const group: TaskGroup = { mainNum, mainTaskIndex: null, mainNeedsAnswer: true, subTasks: [] };
      groupByMain[mainNum] = group;
      taskGroups.push(group);
    }

    if (subNum === '') {
      groupByMain[mainNum].mainTaskIndex = index;
      groupByMain[mainNum].mainNeedsAnswer = task.needsAnswer !== false;
    } else {
      groupByMain[mainNum].subTasks.push({ flatIndex: index, subNum, needsAnswer: task.needsAnswer !== false });
    }
  });

  return { taskGroups, groupByMain };
}

export function getVisibleGroupTaskIndices(group: TaskGroup | null): number[] {
  if (!group) return [];
  const indices: number[] = [];

  if (group.mainTaskIndex !== null && group.mainNeedsAnswer) {
    indices.push(group.mainTaskIndex);
  }

  group.subTasks.forEach(sub => {
    if (sub.needsAnswer) indices.push(sub.flatIndex);
  });

  return indices;
}

export function getGroupLandingTaskIndex(group: TaskGroup | null): number | null {
  if (!group) return null;
  if (group.mainTaskIndex !== null && group.mainNeedsAnswer) return group.mainTaskIndex;
  return group.subTasks.find(sub => sub.needsAnswer)?.flatIndex ?? group.mainTaskIndex;
}
